// frontend/src/components/agent/AgentCustomerDetailModal.jsx
import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import { isFieldVisible, getFieldRange } from './permissionRules';
import ToastHost, { useToast } from '../shared/Toast';

export default function AgentCustomerDetailModal({ user: selectedUser, onClose, onUpdate, perms }) {
  const { toast, showToast } = useToast();
  const [editData, setEditData] = useState({
    name: selectedUser.name,
    username: selectedUser.username,
    new_password: '',
    weekly_limit: selectedUser.weekly_limit,
    min_bet: selectedUser.min_bet,
    max_bet: selectedUser.max_bet,
    parlay_min_bet: selectedUser.parlay_min_bet || 0.01,
    parlay_max_bet: selectedUser.parlay_max_bet || 500,
    live_min_bet: selectedUser.live_min_bet ?? 10,
    live_max_bet: selectedUser.live_max_bet ?? 1000,
    casino_min_bet: selectedUser.casino_min_bet ?? 10,
    casino_max_bet: selectedUser.casino_max_bet ?? 1000,
    casino_weekly_max_win: selectedUser.casino_weekly_max_win ?? 0,
    casino_weekly_max_loss: selectedUser.casino_weekly_max_loss ?? 0,
    is_active: selectedUser.is_active,
    is_read_only: selectedUser.is_read_only || false,
    free_play_loss_percentage: selectedUser.free_play_loss_percentage || 0,
    game_limit: selectedUser.game_limit || 0,
    casino_access: selectedUser.casino_access || false,
  });
  const [cashoutDisabled, setCashoutDisabled] = useState(!!selectedUser.cashout_disabled_for_customer);
  const [balanceAdjustment, setBalanceAdjustment] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [freePlayAdjustment, setFreePlayAdjustment] = useState('');
  const [freePlayReason, setFreePlayReason] = useState('');
  const [pendingBets, setPendingBets] = useState([]);
  const [loadingBets, setLoadingBets] = useState(false);
  const [voidingBetId, setVoidingBetId] = useState(null);

  const canDeleteTransactions = !!perms.can_delete_transactions;

  useEffect(() => {
    if (!canDeleteTransactions) return;
    setLoadingBets(true);
    api.getBetHistory(selectedUser.id, 'pending', 'all')
      .then((res) => setPendingBets(res.single_bets || []))
      .catch(() => setPendingBets([]))
      .finally(() => setLoadingBets(false));
  }, [selectedUser.id, canDeleteTransactions]);

  const handleVoidBet = async (betId) => {
    if (!window.confirm('Void this bet? This cannot be undone.')) return;
    setVoidingBetId(betId);
    try {
      await api.cancelBet(betId);
      setPendingBets((prev) => prev.filter((b) => b.id !== betId));
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to void bet', 'error');
    } finally {
      setVoidingBetId(null);
    }
  };

  const handleUpdate = async () => {
    try {
      const updateData = {};
      if (isFieldVisible('name', perms)) updateData.name = editData.name;
      if (isFieldVisible('username', perms)) updateData.username = editData.username;
      if (isFieldVisible('new_password', perms) && editData.new_password) updateData.new_password = editData.new_password;
      if (isFieldVisible('weekly_limit', perms)) updateData.weekly_limit = editData.weekly_limit;
      if (isFieldVisible('min_bet', perms)) updateData.min_bet = editData.min_bet;
      if (isFieldVisible('max_bet', perms)) updateData.max_bet = editData.max_bet;
      if (isFieldVisible('parlay_min_bet', perms)) updateData.parlay_min_bet = editData.parlay_min_bet;
      if (isFieldVisible('parlay_max_bet', perms)) updateData.parlay_max_bet = editData.parlay_max_bet;
      if (isFieldVisible('live_min_bet', perms)) updateData.live_min_bet = editData.live_min_bet;
      if (isFieldVisible('live_max_bet', perms)) updateData.live_max_bet = editData.live_max_bet;
      if (isFieldVisible('casino_min_bet', perms)) updateData.casino_min_bet = editData.casino_min_bet;
      if (isFieldVisible('casino_max_bet', perms)) updateData.casino_max_bet = editData.casino_max_bet;
      if (isFieldVisible('casino_weekly_max_win', perms)) updateData.casino_weekly_max_win = editData.casino_weekly_max_win;
      if (isFieldVisible('casino_weekly_max_loss', perms)) updateData.casino_weekly_max_loss = editData.casino_weekly_max_loss;
      if (isFieldVisible('is_active', perms)) updateData.is_active = editData.is_active;
      if (isFieldVisible('is_read_only', perms)) updateData.is_read_only = editData.is_read_only;
      if (isFieldVisible('free_play_loss_percentage', perms)) updateData.free_play_loss_percentage = editData.free_play_loss_percentage;
      if (isFieldVisible('game_limit', perms)) updateData.game_limit = editData.game_limit;
      if (isFieldVisible('casino_access', perms)) updateData.casino_access = editData.casino_access;
      if (isFieldVisible('cashout_disabled_for_customer', perms)) updateData.cashout_disabled_for_customer = cashoutDisabled;

      if (isFieldVisible('balance_adjustment', perms) && balanceAdjustment && parseFloat(balanceAdjustment) !== 0) {
        updateData.balance_adjustment = parseFloat(balanceAdjustment);
        if (adjustmentReason) updateData.adjustment_reason = adjustmentReason;
      }
      if (isFieldVisible('free_play_adjustment', perms) && freePlayAdjustment && parseFloat(freePlayAdjustment) !== 0) {
        updateData.free_play_adjustment = parseFloat(freePlayAdjustment);
        if (freePlayReason) updateData.free_play_reason = freePlayReason;
      }

      if (Object.keys(updateData).length === 0) {
        showToast('Nothing to save — enter a change first.', 'error');
        return;
      }

      await api.updateAccount(selectedUser.id, updateData);
      showToast('Customer updated');
      onUpdate();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update account', 'error');
    }
  };

  const rangeHint = (field) => {
    const range = getFieldRange(field, perms);
    if (!range) return null;
    const parts = [];
    if (range.min !== undefined) parts.push(`min $${range.min}`);
    if (range.max !== undefined) parts.push(range.max > 0 ? `max $${range.max}` : 'no max');
    return parts.length ? `Your range: ${parts.join(', ')}` : null;
  };

  const anyEditFieldVisible = Object.keys(editData).some(f => isFieldVisible(f === 'new_password' ? 'new_password' : f, perms))
    || isFieldVisible('cashout_disabled_for_customer', perms);
  const anyFieldVisible = anyEditFieldVisible
    || isFieldVisible('balance_adjustment', perms) || isFieldVisible('free_play_adjustment', perms);

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' };
  const modalStyle = { background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, maxWidth: '56rem', width: '100%', maxHeight: '90vh', overflowY: 'auto', fontFamily };
  const sectionStyle = { background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, padding: '1rem', marginBottom: '1rem' };
  const labelStyle = { display: 'block', marginBottom: '0.3rem', color: colors.textMuted, fontSize: '0.85rem' };
  const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.5rem 0.75rem', width: '100%', fontFamily, boxSizing: 'border-box' };
  const hintStyle = { color: colors.textFaint, fontSize: '0.75rem', marginTop: '0.3rem' };

  return (
    <div style={overlayStyle}>
      <ToastHost toast={toast} />
      <div className="scrollbar-hide" style={modalStyle}>
        <div style={{ padding: '1.25rem', borderBottom: `1px solid ${colors.goldMuted}`, position: 'sticky', top: 0, background: colors.bgGradient }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ color: colors.textPrimary, margin: 0 }}>{selectedUser.name}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textPrimary, fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
          </div>
          <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.3rem' }}>
            Username: {selectedUser.username} | Role: {selectedUser.role}
          </p>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {!anyFieldVisible && (
            <p style={{ color: colors.textMuted, fontFamily }}>You don't have permission to edit any of this customer's settings.</p>
          )}

          {anyEditFieldVisible && (
            <div style={sectionStyle}>
              <h3 style={{ color: colors.textPrimary, marginBottom: '0.35rem' }}>Edit Account Settings</h3>
              <p style={{ ...hintStyle, marginTop: 0, marginBottom: '0.85rem' }}>Enter 0 in any limit field for no limit.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                {isFieldVisible('name', perms) && (
                  <div>
                    <label style={labelStyle}>Name</label>
                    <input style={inputStyle} value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} />
                  </div>
                )}
                {isFieldVisible('username', perms) && (
                  <div>
                    <label style={labelStyle}>Username</label>
                    <input style={inputStyle} value={editData.username} onChange={e => setEditData({ ...editData, username: e.target.value })} />
                  </div>
                )}
                {isFieldVisible('new_password', perms) && (
                  <div>
                    <label style={labelStyle}>New Password</label>
                    <input style={inputStyle} placeholder="Leave blank to keep current" value={editData.new_password} onChange={e => setEditData({ ...editData, new_password: e.target.value })} />
                  </div>
                )}
                {isFieldVisible('weekly_limit', perms) && (
                  <div>
                    <label style={labelStyle}>Weekly Limit</label>
                    <input type="number" style={inputStyle} value={editData.weekly_limit} onChange={e => setEditData({ ...editData, weekly_limit: Number(e.target.value) })} />
                    {rangeHint('weekly_limit') && <p style={hintStyle}>{rangeHint('weekly_limit')}</p>}
                  </div>
                )}
                {isFieldVisible('min_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Min Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.min_bet} onChange={e => setEditData({ ...editData, min_bet: Number(e.target.value) })} />
                    {rangeHint('min_bet') && <p style={hintStyle}>{rangeHint('min_bet')}</p>}
                  </div>
                )}
                {isFieldVisible('max_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Max Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.max_bet} onChange={e => setEditData({ ...editData, max_bet: Number(e.target.value) })} />
                    {rangeHint('max_bet') && <p style={hintStyle}>{rangeHint('max_bet')}</p>}
                  </div>
                )}
                {isFieldVisible('parlay_min_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Parlay Min Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.parlay_min_bet} onChange={e => setEditData({ ...editData, parlay_min_bet: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('parlay_max_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Parlay Max Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.parlay_max_bet} onChange={e => setEditData({ ...editData, parlay_max_bet: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('live_min_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Live Min Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.live_min_bet} onChange={e => setEditData({ ...editData, live_min_bet: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('live_max_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Live Max Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.live_max_bet} onChange={e => setEditData({ ...editData, live_max_bet: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('casino_min_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Casino Min Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.casino_min_bet} onChange={e => setEditData({ ...editData, casino_min_bet: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('casino_max_bet', perms) && (
                  <div>
                    <label style={labelStyle}>Casino Max Bet</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.casino_max_bet} onChange={e => setEditData({ ...editData, casino_max_bet: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('casino_weekly_max_win', perms) && (
                  <div>
                    <label style={labelStyle}>Casino Weekly Max Win</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.casino_weekly_max_win} onChange={e => setEditData({ ...editData, casino_weekly_max_win: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('casino_weekly_max_loss', perms) && (
                  <div>
                    <label style={labelStyle}>Casino Weekly Max Loss</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.casino_weekly_max_loss} onChange={e => setEditData({ ...editData, casino_weekly_max_loss: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('free_play_loss_percentage', perms) && (
                  <div>
                    <label style={labelStyle}>Free Play Loss Rebate (%)</label>
                    <input type="number" min="0" max="100" style={inputStyle} value={editData.free_play_loss_percentage} onChange={e => setEditData({ ...editData, free_play_loss_percentage: Number(e.target.value) })} />
                  </div>
                )}
                {isFieldVisible('game_limit', perms) && (
                  <div>
                    <label style={labelStyle}>Contest Limit</label>
                    <input type="number" step="0.01" style={inputStyle} value={editData.game_limit} onChange={e => setEditData({ ...editData, game_limit: Number(e.target.value) })} />
                  </div>
                )}
              </div>

              {(isFieldVisible('casino_access', perms) || isFieldVisible('is_active', perms) || isFieldVisible('is_read_only', perms) || isFieldVisible('cashout_disabled_for_customer', perms)) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem 1.5rem', marginTop: '1.25rem', paddingTop: '1rem', borderTop: `1px solid ${colors.goldMuted}` }}>
                  {isFieldVisible('casino_access', perms) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={editData.casino_access} onChange={e => setEditData({ ...editData, casino_access: e.target.checked })} />
                      Casino Access
                    </label>
                  )}
                  {isFieldVisible('is_active', perms) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={editData.is_active} onChange={e => setEditData({ ...editData, is_active: e.target.checked })} />
                      Account Active
                    </label>
                  )}
                  {isFieldVisible('is_read_only', perms) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={editData.is_read_only} onChange={e => setEditData({ ...editData, is_read_only: e.target.checked })} />
                      Read-Only
                    </label>
                  )}
                  {isFieldVisible('cashout_disabled_for_customer', perms) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={cashoutDisabled} onChange={e => setCashoutDisabled(e.target.checked)} />
                      Disable Cashout for This Customer
                    </label>
                  )}
                </div>
              )}
            </div>
          )}

          {isFieldVisible('balance_adjustment', perms) && (
            <div style={sectionStyle}>
              <h3 style={{ color: colors.textPrimary, marginBottom: '0.75rem' }}>Balance Adjustment</h3>
              <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                Current Balance: <span style={{ fontWeight: 700, color: (selectedUser.weekly_balance ?? 0) >= 0 ? colors.odds : colors.negative }}>${(selectedUser.weekly_balance ?? 0).toFixed(2)}</span>
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Amount</label>
                  <input type="number" step="0.01" placeholder="e.g., 50 or -25" style={inputStyle} value={balanceAdjustment} onChange={e => setBalanceAdjustment(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Reason (optional)</label>
                  <input style={inputStyle} value={adjustmentReason} onChange={e => setAdjustmentReason(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {isFieldVisible('free_play_adjustment', perms) && (
            <div style={sectionStyle}>
              <h3 style={{ color: colors.textPrimary, marginBottom: '0.75rem' }}>Free Play Balance</h3>
              <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                Current: ${(selectedUser.free_play_balance || 0).toFixed(2)}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Amount</label>
                  <input type="number" step="0.01" min="0" style={inputStyle} value={freePlayAdjustment} onChange={e => setFreePlayAdjustment(e.target.value)} />
                </div>
                <div>
                  <label style={labelStyle}>Reason (optional)</label>
                  <input style={inputStyle} value={freePlayReason} onChange={e => setFreePlayReason(e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {anyFieldVisible && (
            <button
              onClick={handleUpdate}
              style={{ marginBottom: '1rem', background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.7rem 1.75rem', cursor: 'pointer', fontWeight: 700, fontFamily, fontSize: '0.95rem', width: '100%' }}
            >
              Save Changes
            </button>
          )}

          {canDeleteTransactions && (
            <div style={sectionStyle}>
              <h3 style={{ color: colors.textPrimary, marginBottom: '0.75rem' }}>Pending Bets</h3>
              {loadingBets ? (
                <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Loading...</p>
              ) : pendingBets.length === 0 ? (
                <p style={{ color: colors.textMuted, fontSize: '0.85rem' }}>No pending bets.</p>
              ) : (
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {pendingBets.map((bet) => (
                    <div key={bet.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: colors.cardBg, borderRadius: 6, padding: '0.5rem 0.75rem' }}>
                      <span style={{ color: colors.textPrimary, fontSize: '0.85rem' }}>{bet.description} — ${bet.amount.toFixed(2)} ({bet.odds})</span>
                      <button
                        onClick={() => handleVoidBet(bet.id)}
                        disabled={voidingBetId === bet.id}
                        style={{ background: 'rgba(239,68,68,0.2)', border: `1px solid ${colors.negative}`, color: colors.negative, borderRadius: 6, padding: '0.3rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        {voidingBetId === bet.id ? 'Voiding...' : 'Void'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
